import { Injectable } from '@angular/core';
import {
  CreateUserMemoryInput,
  CreateUserMemoryMutation,
  ImpersonateUserInput,
  ImpersonateUserMutation,
  MergeUsersInput,
  MergeUsersMutation,
  RequestPasswordResetInput,
  RequestPasswordResetMutation,
  SearchUsersQuery,
  SearchUsersWithIlClassAccessQuery,
  SignOutInput,
  SignOutMutation
} from '@queries/generated';
import { map } from 'rxjs/operators';

interface SearchUsersInput {
  query: string;
  limit?: number;
  includeNeverLoggedIn?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  constructor(
    private impersonateUserMutation: ImpersonateUserMutation,
    private signOutMutation: SignOutMutation,
    private mergeUsersMutation: MergeUsersMutation,
    private searchUsersQuery: SearchUsersQuery,
    private searchUsersWithILClassQuery: SearchUsersWithIlClassAccessQuery,
    private requestPasswordResetMutation: RequestPasswordResetMutation,
    private createUserMemoryMutation: CreateUserMemoryMutation
  ) {}

  impersonate = (input: ImpersonateUserInput) => {
    return this.impersonateUserMutation.mutate({ input }).pipe(map((resp) => resp.data.impersonateUser));
  };

  logout(input: SignOutInput) {
    return this.signOutMutation.mutate({ input }).pipe(
      map(({ data }) => {
        const { redirectTo, success } = data.signOut;

        if (success && redirectTo) {
          return redirectTo;
        }
      })
    );
  }

  merge(input: MergeUsersInput) {
    return this.mergeUsersMutation.mutate({ input }).pipe(map(({ data }) => data.mergeUsers.success));
  }

  search(variables: SearchUsersInput) {
    return this.searchUsersQuery.watch(variables).valueChanges.pipe(map(({ data }) => data.searchUsers));
  }

  searchByiLClassAccess = (ilClassId: string, query: string) => {
    return this.searchUsersWithILClassQuery.watch({ ilClassId, query }).valueChanges.pipe(
      map(({ data }) => data.users.searchByIlClassAccess.nodes),
      map((users) => {
        return users.flatMap((user) => {
          return user.grantsAvailableForClass.map((grant) => ({ ...user, grant }));
        });
      })
    );
  };

  requestPasswordReset(input: RequestPasswordResetInput) {
    return this.requestPasswordResetMutation.mutate({ input }).pipe(map(({ data }) => data.requestPasswordReset.ok));
  }

  remember = (input: CreateUserMemoryInput) => {
    return this.createUserMemoryMutation
      .mutate({ input }, { refetchQueries: ['LoadCurrentUser'] })
      .pipe(map(({ data }) => data.createUserMemory.userMemory));
  };
}
